import {
  Box,
  Hidden,
  IconButton,
  makeStyles,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { Menu } from '@material-ui/icons';
import classNames from 'classnames';
import { usePrevious } from 'hooks';
import { checkUser } from 'lib/Api/User';
import { getUserData } from 'lib/Helper/Auth';
import * as React from 'react';
import {
  BreadcrumbsItem,
  BreadcrumbsProvider,
} from 'react-breadcrumbs-dynamic';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { ClientsView } from 'views';
import QuoteGeneratorView from 'views/QuoteGenerator/QuoteGeneratorView';
import CommercialSupportView from 'views/ClientAllocation/ClientAllocationView';
import { DashboardView } from 'views/Dashboard/DashboardView';
import { DashboardsView } from 'views/Dashboards/DashboardsView';
import { MeetingsView } from 'views/Meetings';
import { DealsView } from 'views/Deals';
import { NpsView } from 'views/NPS';
import { SpreadsheetsView } from 'views/Spreadsheets';
import { Breadcrumbs, Sidebar, UpdateNotification } from './components';
import HelpbotView from 'views/Helpbot/HelpbotView';
import PermissionsView from 'views/Permissions/PermissionsView';
import { DriveFolderView } from 'views/Drive/DriveFolderView';
import { TermsGeneratorView } from 'views/TermsGenerator/TermsGeneratorView';
import { PlaygroundView } from 'views/Playground/PlaygroundView';
import InterviewReportGeneratorView from 'views/InterviewReportGenerator/InterviewReportGeneratorView';
import CvCritiqueView from 'views/CvCritique/CvCritiqueView';
import VacancyMatcherView from 'views/VacancyMatcher/VacancyMatcherView';
import { FeedbackView } from 'views';
import { BrandsView } from 'views/OurBrands/BrandsView';

interface Props {
  componentError?: Error;

  updateAvailable: boolean;
  onUpdate(): void;
}

const useStyles = makeStyles((theme) => ({
  main: {
    height: '100%',
    color: theme.palette.text.primary,
    padding: theme.spacing(2),
    [theme.breakpoints.up('lg')]: {
      marginLeft: (props: { sidebarCollapsed: boolean }) =>
        props.sidebarCollapsed ? 80 : theme.drawerWidth,
      paddingBottom: theme.spacing(5),
    },
  },
  trialMain: {
    opacity: 0.2,
  },
  noSidebar: {
    [theme.breakpoints.up('lg')]: {
      marginLeft: 0,
    },
  },
  content: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    paddingBottom: theme.spacing(8.5),
  },
  loadingWrapper: {
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  offsetTrialBanner: {
    paddingBottom: theme.spacing(7.5),

    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(10),
    },
  },
  offsetSampleDataTB: {
    paddingBottom: theme.spacing(7.5),

    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(15),
    },
  },
  menuButton: {
    marginLeft: theme.spacing(1) * -1,
    marginRight: theme.spacing(0.5),
  },
  headerToolbar: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: 0,
  },
}));

export const AppLayout: React.FC<Props> = ({ updateAvailable, onUpdate }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const classes = useStyles({ sidebarCollapsed });
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const { search } = useLocation();

  const [redirectAfterLogin, setRedirectAfterLogin] = React.useState<
    string | undefined
  >();

  const authUser = getUserData();
  const prevToken = usePrevious(authUser?.token);

  const iFrame =
    new URLSearchParams(useLocation().search).get('iframe') === '1'
      ? true
      : false;

  React.useEffect(() => {
    if (authUser && authUser.token !== prevToken) {
      checkUser(authUser.token);
    }
  }, [authUser, prevToken]);

  React.useEffect(() => {
    if (!redirectAfterLogin || redirectAfterLogin === '/dashboard') {
      const urlSearchParams = new URLSearchParams(search);
      const prevRedirect = urlSearchParams.get('r');
      const prevQueryParams = urlSearchParams.get('q');

      if (prevRedirect && prevRedirect.trim().length > 0) {
        setRedirectAfterLogin(
          (prevRedirect === '/' || !prevRedirect
            ? '/dashboard'
            : prevRedirect) +
            (prevQueryParams ? decodeURIComponent(prevQueryParams) : ''),
        );
      }
    } else if (!redirectAfterLogin) {
      setRedirectAfterLogin('/dashboard');
    }
  }, [redirectAfterLogin, search]);

  return (
    <React.Fragment>
      <Sidebar
        open={drawerOpen}
        openDrawer={openDrawer}
        closeDrawer={closeDrawer}
        onCollapseChange={setSidebarCollapsed}
      />

      <BreadcrumbsProvider>
        <main
          id="main"
          className={classNames({
            [classes.main]: true,
            iframe: iFrame,
          })}
        >
          <Toolbar className={classes.headerToolbar}>
            <Hidden lgUp>
              <IconButton
                color="inherit"
                className={classes.menuButton}
                onClick={toggleDrawer}
              >
                <Menu />
              </IconButton>
            </Hidden>
            <Breadcrumbs />
          </Toolbar>
          {updateAvailable && (
            <UpdateNotification updateServiceWorker={onUpdate} />
          )}

          <Switch>
            {' '}
            <Route exact path="/">
              <Redirect to="/dashboard" />
            </Route>
            <Route path="/dashboard">
              <BreadcrumbsItem to="/dashboard">Dashboard</BreadcrumbsItem>
              <DashboardView />
            </Route>
            <Route path="/clients">
              <BreadcrumbsItem to="/clients">Clients</BreadcrumbsItem>
              <ClientsView />
            </Route>
            <Route path="/chatbot">
              <BreadcrumbsItem to="/chatbot">AI Assistant</BreadcrumbsItem>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="60vh"
                style={{ gap: 12, padding: '0 24px' }}
              >
                <Typography variant="h5">
                  AI Assistant is temporarily unavailable
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  align="center"
                  style={{ maxWidth: 480 }}
                >
                  I am working on improving the quality of the AI Assistant. It
                  will be back once it&apos;s ready - thanks for your patience.
                </Typography>
              </Box>
            </Route>
            <Route path="/dashboards">
              <BreadcrumbsItem to="/dashboards">Dashboards</BreadcrumbsItem>
              <DashboardsView />
            </Route>
            <Route path="/resources">
              <BreadcrumbsItem to="/resources">
                Tools & Resources
              </BreadcrumbsItem>
              <SpreadsheetsView />
            </Route>
            <Route path="/nps/:npsId?">
              <BreadcrumbsItem to="/nps">NPS</BreadcrumbsItem>
              <NpsView />
            </Route>
            <Route path="/brands">
              <BreadcrumbsItem to="/brands">Our Brands</BreadcrumbsItem>
              <BrandsView />
            </Route>
            <Route path="/deals">
              <BreadcrumbsItem to="/deals">Deals</BreadcrumbsItem>
              <DealsView />
            </Route>
            <Route path="/meetings">
              <BreadcrumbsItem to="/meetings">Meetings</BreadcrumbsItem>
              <MeetingsView />
            </Route>
            <Route path="/permissions">
              <BreadcrumbsItem to="/permissions">Permissions</BreadcrumbsItem>
              <PermissionsView />
            </Route>
            <Route exact path="/playground">
              <BreadcrumbsItem to="/playground">Playground</BreadcrumbsItem>
              <PlaygroundView />
            </Route>
            <Route path="/playground/help">
              <BreadcrumbsItem to="/playground">Playground</BreadcrumbsItem>
              <BreadcrumbsItem to="/playground/help">
                Help Center Bot
              </BreadcrumbsItem>
              <HelpbotView />
            </Route>
            <Route path="/playground/interview-report-generator">
              <BreadcrumbsItem to="/playground">Playground</BreadcrumbsItem>
              <BreadcrumbsItem to="/playground/interview-report-generator">
                Interview Report Generator
              </BreadcrumbsItem>
              <InterviewReportGeneratorView />
            </Route>
            <Route path="/playground/cv-critique">
              <BreadcrumbsItem to="/playground">Playground</BreadcrumbsItem>
              <BreadcrumbsItem to="/playground/cv-critique">
                CV Critique
              </BreadcrumbsItem>
              <CvCritiqueView />
            </Route>
            <Route path="/playground/vacancy-matcher">
              <BreadcrumbsItem to="/playground">Playground</BreadcrumbsItem>
              <BreadcrumbsItem to="/playground/vacancy-matcher">
                Matching
              </BreadcrumbsItem>
              <VacancyMatcherView />
            </Route>
            {/* old links, before the two matchers became tabs */}
            <Route path="/playground/vacancy-candidates">
              <Redirect to="/playground/vacancy-matcher#candidates" />
            </Route>
            <Route path="/playground/candidate-vacancies">
              <Redirect to="/playground/vacancy-matcher#vacancies" />
            </Route>
            <Route path="/proposal-generator">
              <BreadcrumbsItem to="/proposal-generator">
                Proposal Management
              </BreadcrumbsItem>
              <QuoteGeneratorView />
            </Route>
            <Route path="/commercial-support-utility">
              <BreadcrumbsItem to="/commercial-support-utility">
                Client Allocation
              </BreadcrumbsItem>
              <CommercialSupportView />
            </Route>
            <Route path="/drive-folders">
              <BreadcrumbsItem to="/drive-folders">
                Folder Management
              </BreadcrumbsItem>
              <DriveFolderView />
            </Route>
            <Route path="/drive-terms">
              <BreadcrumbsItem to="/drive-terms">
                Agreement Management
              </BreadcrumbsItem>
              <TermsGeneratorView />
            </Route>
            <Route path="/feedback">
              <BreadcrumbsItem to="/feedback">Feedback</BreadcrumbsItem>
              <FeedbackView />
            </Route>
            {redirectAfterLogin && (
              <Route>
                <Redirect to={redirectAfterLogin ?? '/dashboard'} />
              </Route>
            )}
          </Switch>
          <div style={{ height: 20 }} />
        </main>
      </BreadcrumbsProvider>
    </React.Fragment>
  );

  function toggleDrawer() {
    setDrawerOpen((open) => !open);
  }

  function openDrawer() {
    if (!drawerOpen) {
      toggleDrawer();
    }
  }

  function closeDrawer() {
    if (drawerOpen) {
      toggleDrawer();
    }
  }
};
